import { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import Header from '@/components/Header';
import { submitContactForm } from '@/services/api';
import { Footer } from './HomeFooter';

const contactSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  email: z.string().email('Invalid email address'),
  company: z.string().optional(),
  message: z.string().min(10, 'Message must be at least 10 characters'),
});


const Contact = () => {
  const [submitStatus, setSubmitStatus] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: zodResolver(contactSchema),
  });

  const onSubmit = async (data) => {
    setSubmitStatus('loading');
    setErrorMessage('');

    try {
      await submitContactForm(data);
      setSubmitStatus('success');
      reset();
      setTimeout(() => setSubmitStatus('idle'), 5000);
    } catch (err) {
      setSubmitStatus('error');
      setErrorMessage(err instanceof Error ? err.message : 'Failed to send message');
    }
  };

  return (
    <div className="bg-[#0a0a0b] text-white overflow-hidden">
      <Header />
      <HeroSection />
      <FormSection onSubmit={handleSubmit(onSubmit)} register={register} errors={errors} submitStatus={submitStatus} errorMessage={errorMessage} />
      <Footer />
    </div>
  );
};

const HeroSection = () => {
  return (
    <section className="relative flex items-center justify-center overflow-hidden py-12 md:py-16">
      <motion.div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0a0a0b]/50 to-[#0a0a0b] z-10" />
      </motion.div>

      <div className="absolute inset-0 z-10 opacity-20">
        <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)',
          backgroundSize: '100px 100px'
        }} />
      </div>

      <div className="container mx-auto px-6 z-20 text-center">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.2 }}
        >
          <h1 className="text-6xl md:text-7xl font-bold mb-6 leading-tight tracking-tight">
            Let's Build
            <br />
            <span className="bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-500 bg-clip-text text-transparent">
              Together
            </span>
          </h1>

          <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed">
            Have a project in mind? Let's discuss how we can help transform your vision into nation-scale reality.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

const FormSection = ({ onSubmit, register, errors, submitStatus, errorMessage }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section ref={ref} className="py-20 bg-gradient-to-b from-[#0a0a0b] to-[#0f0f10]">
      <div className="container mx-auto px-6 max-w-2xl">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4">Get in Touch</h2>
          <p className="text-lg text-gray-400">
            Fill out the form below and we'll get back to you within 24 hours.
          </p>
        </motion.div>

        <motion.form
          onSubmit={onSubmit}
          className="space-y-6 p-8 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <div className="space-y-2">
            <Label htmlFor="name" className="text-white">Name *</Label>
            <Input
              id="name"
              placeholder="Your name"
              {...register('name')}
              className={cn(
                'bg-white/5 border-white/10 text-white placeholder:text-gray-500',
                errors.name && 'border-red-500'
              )}
            />
            {errors.name && <p className="text-red-400 text-sm">{errors.name.message}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-white">Email *</Label>
            <Input
              id="email"
              type="email"
              placeholder="your@email.com"
              {...register('email')}
              className={cn(
                'bg-white/5 border-white/10 text-white placeholder:text-gray-500',
                errors.email && 'border-red-500'
              )}
            />
            {errors.email && <p className="text-red-400 text-sm">{errors.email.message}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="company" className="text-white">Company</Label>
            <Input
              id="company"
              placeholder="Your company (optional)"
              {...register('company')}
              className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="message" className="text-white">Message *</Label>
            <Textarea
              id="message"
              placeholder="Tell us about your project..."
              rows={6}
              {...register('message')}
              className={cn(
                'bg-white/5 border-white/10 text-white placeholder:text-gray-500 resize-none',
                errors.message && 'border-red-500'
              )}
            />
            {errors.message && <p className="text-red-400 text-sm">{errors.message.message}</p>}
          </div>

          {submitStatus === 'success' && (
            <motion.div
              className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg text-green-400"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              Message sent! We'll be in touch.
            </motion.div>
          )}

          {submitStatus === 'error' && (
            <motion.div
              className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {errorMessage || 'Failed to send message. Please try again.'}
            </motion.div>
          )}

          <Button
            type="submit"
            disabled={submitStatus === 'loading'}
            className="w-full bg-white text-black hover:bg-gray-200 py-6 text-lg group transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitStatus === 'loading' ? 'Sending...' : 'Send Message'}
            {submitStatus !== 'loading' && <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />}
          </Button>
        </motion.form>
      </div>
    </section>
  );
};

export default Contact;
